import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  type ConfirmationResult,
  type User,
  RecaptchaVerifier,
  onAuthStateChanged,
  signInWithPhoneNumber,
  signOut,
} from 'firebase/auth'
import { auth, firebaseConfigured } from '../lib/firebase'

type AuthValue = {
  user: User | null
  loading: boolean
  otpSent: boolean
  sendOtp: (phone: string) => Promise<void>
  verifyOtp: (otp: string) => Promise<void>
  logout: () => Promise<void>
  resetOtp: () => void
}

const AuthContext = createContext<AuthValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [otpSent, setOtpSent] = useState(false)
  const confirmation = useRef<ConfirmationResult | null>(null)
  const verifier = useRef<RecaptchaVerifier | null>(null)

  useEffect(() => onAuthStateChanged(auth, (nextUser) => {
    setUser(nextUser)
    setLoading(false)
  }), [])

  const sendOtp = async (phone: string) => {
    if (!firebaseConfigured) throw new Error('Firebase keys अभी connect नहीं की गई हैं। Setup guide देखें।')
    verifier.current?.clear()
    verifier.current = new RecaptchaVerifier(auth, 'recaptcha-container', { size: 'invisible' })
    const normalized = phone.trim().startsWith('+') ? phone.trim() : `+91${phone.replace(/\D/g, '')}`
    confirmation.current = await signInWithPhoneNumber(auth, normalized, verifier.current)
    setOtpSent(true)
  }

  const verifyOtp = async (otp: string) => {
    if (!confirmation.current) throw new Error('पहले OTP भेजें।')
    await confirmation.current.confirm(otp.trim())
    setOtpSent(false)
  }

  const resetOtp = () => {
    setOtpSent(false)
    confirmation.current = null
    verifier.current?.clear()
    verifier.current = null
  }

  const value = useMemo(() => ({
    user, loading, otpSent, sendOtp, verifyOtp,
    logout: () => signOut(auth), resetOtp,
  }), [user, loading, otpSent])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const value = useContext(AuthContext)
  if (!value) throw new Error('useAuth must be used inside AuthProvider')
  return value
}
